import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * 운동종목 펼침형 다중 선택. 회원상세 개인레슨 카드의 담당강사 배정(TrainerSearchSelect)과 같은 구성이다.
 * 한 번 펼친 상태에서 여러 종목을 연속으로 체크/해제할 수 있다.
 * 종목은 지점당 2~5개(전체 13개) 수준이라 검색창·초성 헤더는 두지 않는다.
 *
 * 값 계약은 호출부가 정한다 — onChange 는 항상 선택 순서를 보존한 배열을 넘기고,
 * 빈 배열을 '' 로 바꿀지는 각 상품 화면(기존 규약)이 결정한다.
 */
export const SportTypeSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = '운동종목을 선택해 주세요.',
  hasError = false,
}) => {
  const [open, setOpen] = useState(false);

  const selected = Array.isArray(value) ? value : [];
  const selectedLabels = selected
    .map(sport => options.find(option => option.value === sport)?.label)
    .filter(Boolean)
    .join(', ');

  const borderColor = hasError ? '#dc2626' : '#0F7F85';

  const toggle = sport => {
    // 선택 순서 보존 — 새로 고른 종목은 뒤에 붙인다
    onChange(selected.includes(sport) ? selected.filter(item => item !== sport) : [...selected, sport]);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* 헤더 (클릭하면 펼침/접힘) */}
      <Box
        onClick={() => setOpen(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '44px',
          px: '14px',
          border: open || hasError ? `1.5px solid ${borderColor}` : '1px solid #c4c4c4',
          borderRadius: open ? '6px 6px 0 0' : '6px',
          cursor: 'pointer',
          backgroundColor: selected.length ? '#f0f8f8' : '#fff',
          transition: 'all 0.15s ease',
          '&:hover': { borderColor: '#0F7F85' },
        }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: selected.length ? 500 : 400,
            color: selected.length ? '#0F7F85' : '#999',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {selectedLabels || placeholder}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: open ? '#0F7F85' : 'transparent',
            transition: 'all 0.15s ease',
          }}>
          {open ? (
            <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />
          )}
        </Box>
      </Box>

      {/* 패널 (펼쳐졌을 때) */}
      {open && (
        <Box
          sx={{
            border: `1.5px solid ${borderColor}`,
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            maxHeight: '280px',
            overflowY: 'auto',
          }}>
          {options.length === 0 ? (
            <Box sx={{ p: '24px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: '#999' }}>선택할 운동종목이 없습니다</Typography>
            </Box>
          ) : (
            options.map(option => {
              const isSelected = selected.includes(option.value);
              return (
                <Box
                  key={option.value}
                  onClick={() => toggle(option.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    px: '14px',
                    py: '10px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#f0f8f8' : '#fff',
                    borderBottom: '1px solid #f5f5f5',
                    '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                  {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#0F7F85', flexShrink: 0 }} />}
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#333',
                      fontWeight: isSelected ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {option.label}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

export default SportTypeSelect;
